import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { userNotificationStateApi } from '../services/api';
import { dispatchNotificationRefresh, useSidebarBadges } from '../contexts/NotificationStateContext';

// Valid page types that support server-side badge tracking
// REQUISITO: Solo inbox y reminders tienen badges
const BADGE_PAGE_TYPES = ['inbox', 'reminders'] as const;
type BadgePageType = typeof BADGE_PAGE_TYPES[number];

// Navigation items mapping - route path to page type
// Only pages with badges return a BadgePageType; others return undefined
const NAV_ITEMS: Record<string, BadgePageType | undefined> = {
  '/': 'inbox',           // Has badges
  '/today': undefined,    // No badges
  '/upcoming': undefined, // No badges
  '/reminders': 'reminders', // Has badges
  // These pages don't have badges, so they map to undefined
  '/recurring': undefined,
  '/projects': undefined,
  '/history': undefined,
  '/statistics': undefined,
  '/users': undefined,
  '/attendances': undefined,
  '/settings': undefined,
};

interface UsePageSeenOptions {
  /** Delay in ms before marking as seen (default: 100ms for faster response) */
  delay?: number;
  /** Custom condition - only mark as seen when true */
  hasLoadedContent?: boolean;
}

/**
 * Hook to mark a page as "seen" after content has loaded.
 * 
 * Server-side implementation: Calls the server API to update the
 * UserNotificationState record, which clears the badge for that page.
 * This ensures cross-device consistency and prevents race conditions.
 * 
 * Only tracks "seen" state for pages that have notification badges:
 * - inbox, reminders
 */
export function usePageSeen(options: UsePageSeenOptions = {}) {
  const { delay = 100, hasLoadedContent = true } = options;
  const location = useLocation();
  
  // Get access to badge state for optimistic updates
  // This hook is always used within NotificationStateProvider context
  const { updateBadges, badges: currentBadges } = useSidebarBadges();
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const markedRef = useRef(false);

  useEffect(() => {
    // Reset the marked flag when route changes
    markedRef.current = false;

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Only proceed if content has loaded
    if (!hasLoadedContent) return;

    // Get the nav item key for the current path
    const navKey = NAV_ITEMS[location.pathname];
    if (!navKey) return;

    // Only mark as seen for items that have badges
    if (!BADGE_PAGE_TYPES.includes(navKey as BadgePageType)) return;


    // Optimistically clear the badge immediately so the user sees instant feedback
    updateBadges({ ...currentBadges, [navKey]: 0 });

    // Mark as seen immediately (or after specified delay) to clear the badge
    timeoutRef.current = setTimeout(async () => {
      if (!markedRef.current) {
        try {
          // Call server API directly to mark page as seen
          await userNotificationStateApi.markPageSeen(navKey);
          markedRef.current = true;
          // Trigger badge refresh in context to sync with server
          dispatchNotificationRefresh();
        } catch (error) {
          console.error('[usePageSeen] Error marking page as seen:', error);
          // On error, refresh to restore correct state
          dispatchNotificationRefresh();
        }
      }
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location.pathname, hasLoadedContent, delay]);
}

/**
 * Simple hook that marks the current page as seen immediately.
 * Use this for pages that don't have async content loading.
 */
export function useMarkPageSeenImmediately() {
  const location = useLocation();

  useEffect(() => {
    const navKey = NAV_ITEMS[location.pathname];
    if (navKey && BADGE_PAGE_TYPES.includes(navKey as BadgePageType)) {
      // Mark immediately for static content
      userNotificationStateApi.markPageSeen(navKey)
        .then(() => dispatchNotificationRefresh())
        .catch(err => console.error('[useMarkPageSeenImmediately] Error:', err));
    }
  }, [location.pathname]);
}

export default usePageSeen;